import { useMutation } from '@apollo/client';
import type { UpdateControlledRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlledRiskInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateRiskInternalAuditResultInput } from './useUpdateControlledRiskInternalAuditResultTRPC';
import { useUpdateControlledRiskInternalAuditResultTRPC } from './useUpdateControlledRiskInternalAuditResultTRPC';

export const useUpdateControlledRiskInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateControlledRiskInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_controlled_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'risk_controlled_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateControlledRiskInternalAuditResultTRPC();

  const updateControlledRiskInternalAuditResult = async (
    variables: UpdateRiskInternalAuditResultInput
  ): Promise<UpdateControlledRiskInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateControlledRiskInternalAuditResult(variables);
    }

    const result = await updateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update controlled risk internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateControlledRiskInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControlledRiskInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
